import React, { StrictMode, Suspense } from 'react';
import moment from 'moment';
import { LocaleObject, setLocale } from 'yup';
import { Provider as ReduxProvider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import * as Sentry from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { reactPlugin } from '@datadog/browser-rum-react';
import { yupMessages } from '@whitelabel/helpers/forms/messages';
import { theme } from '@whitelabel/rentalcover-shared/theme';
import { MediaContextProvider, mediaStyles } from '@whitelabel/helpers/media';
import Loading from '@whitelabel/rentalcover-shared/components/Loading';
import { site } from '@whitelabel/helpers/site';
import { CURRENT_LOCALE } from '@whitelabel/rentalcover-shared/helpers/constants';
import GlobalStyle from '@whitelabel/rentalcover-shared/styled/GlobalStyle';
import { loadCountries, captureExceptionWithRecording } from '@whitelabel/helpers/utils';
import { getClaimsActor, setClaimsActor } from '@whitelabel/cp-shared/helpers/agentSession';
import { logErrorToDatadog } from '@whitelabel/cp-shared/helpers/datadogErrorLogging';
import { loadPolyfills, loadMomentLocale } from '@whitelabel/xcover-shared/helpers/polyfills';
import ErrorBoundary from './containers/ErrorBoundary';
import App from './containers/App';
import { store } from './store';
import '@whitelabel/rentalcover-shared/scss/index.scss';
import { localePrefix } from './helpers/constants';
import IntlConfigProvider from './components/IntlConfigProvider';
import { detectLanguage } from './helpers/site';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracesSampleRate: 1.0,
  attachStacktrace: true,
  ignoreErrors: [/(\$|jQuery) is not defined/i, /Can't find variable: (\$|jQuery)/i],
});

void (async function bootstrapApp() {
  const locale = detectLanguage();
  if (!locale) return;
  site.locale = locale;

  await loadPolyfills(locale);
  await loadMomentLocale(locale);
  try {
    if (!window.isHeadless && import.meta.env.VITE_DD_CLIENT_TOKEN) {
      const datadogSite = 'datadoghq.eu';
      const serviceName = 'rentalcover-claims-portal';
      const datadogEnv = import.meta.env.VITE_ENV;
      const clientToken = import.meta.env.VITE_DD_CLIENT_TOKEN as string;

      datadogLogs.init({
        clientToken,
        site: datadogSite,
        service: serviceName,
        env: datadogEnv,
        forwardErrorsToLogs: false,
        sessionSampleRate: 100,
      });

      datadogRum.init({
        applicationId: import.meta.env.VITE_DD_APPLICATION_ID as string,
        clientToken,
        site: datadogSite,
        service: serviceName,
        env: datadogEnv,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        enablePrivacyForActionName: true,
        sessionSampleRate: 100,
        sessionReplaySampleRate: import.meta.env.VITE_ENV === 'staging' ? 100 : 5,
        traceSampleRate: import.meta.env.VITE_ENV === 'staging' ? 100 : 5,
        traceContextInjection: 'sampled',
        defaultPrivacyLevel: 'mask-user-input',
        // Exclude both brightwrite.com and brightwrite-data.com anywhere in the full URL
        excludedActivityUrls: [/brightwrite(-data)?\.com/i],
        plugins: [reactPlugin({ router: false })],
      });
    }
    // A reload keeps the agent token in sessionStorage but RUM and BW start with an empty context.
    const claimsActor = getClaimsActor();
    if (claimsActor) setClaimsActor(claimsActor);
  } catch (error) {
    const err = error as Error;
    logErrorToDatadog(err, { source: 'datadog_init_error' });
    captureExceptionWithRecording(err);
  }
  site.countries = await loadCountries(locale);
  setLocale(yupMessages as LocaleObject);
  sessionStorage.setItem(CURRENT_LOCALE, locale);

  if (locale === 'ar') {
    moment.updateLocale('ar', {
      weekdaysMin: 'الأحد_الإثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت'.split('_'),
      preparse: (string: string) => string,
      postformat: (string: string) => string,
    });
  }

  const root = createRoot(document.getElementById('root')!);
  root.render(
    <ReduxProvider store={store}>
      <ThemeProvider theme={theme}>
        <style type="text/css">{mediaStyles}</style>
        <MediaContextProvider>
          <GlobalStyle />
          <StrictMode>
            <BrowserRouter>
              <Switch>
                <Route path={localePrefix}>
                  <IntlConfigProvider>
                    <Suspense fallback={<Loading />}>
                      <ErrorBoundary>
                        <App />
                      </ErrorBoundary>
                    </Suspense>
                  </IntlConfigProvider>
                </Route>
              </Switch>
            </BrowserRouter>
          </StrictMode>
        </MediaContextProvider>
      </ThemeProvider>
    </ReduxProvider>,
  );
})();
